/**
 * The Artifacts tab (f15.1) on Business Unit Group, Business Unit and Client
 * detail. Mirrors the prototype's `client-detail.html` Artifacts panel.
 *
 * The Group and Unit tabs list what hangs off that node; the Client tab also
 * lists what hangs off its engagements, with a "Hangs off" column
 * (Q-f15.1-4). Rows come newest upload first, already sorted by the server.
 *
 * f15.3 adds the Published version and State columns, read from the
 * derived `published_state` and `published_version`.
 */

import { useMemo, useState } from 'react';
import { ArrowRight, FileText, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import type { ArtifactListItem, ArtifactListOwnerType } from '@/api/client';
import { useArtifactsForOwner, useClientEngagements } from '@/api/queries';
import { PUBLISH_ARTIFACT, rowCan } from '@/session/permissions';

import { formatDate, levelLabel, typeLabel } from './artifactView';
import { PublicationBadge } from './PublicationControls';
import { NewArtifactDialog, type OwnerOption } from './NewArtifactDialog';

const ACTIVE = 'ACTIVE';

function useOwnerOptions(
  ownerType: ArtifactListOwnerType,
  ownerId: string,
  ownerName: string,
  mayPublish: boolean,
  clientId: string | undefined,
): OwnerOption[] {
  const engagements = useClientEngagements(clientId ?? '', { enabled: Boolean(clientId) });
  return useMemo(() => {
    const options: OwnerOption[] = [];
    if (mayPublish) options.push({ type: ownerType, id: ownerId, label: `${ownerName} (${levelLabel(ownerType)})` });
    for (const engagement of engagements.data?.engagements ?? []) {
      if (engagement.status.code !== ACTIVE || !rowCan(engagement, PUBLISH_ARTIFACT)) continue;
      options.push({ type: 'ENGAGEMENT', id: engagement.id, label: `${engagement.name} · ${engagement.code}` });
    }
    return options;
  }, [engagements.data, mayPublish, ownerId, ownerName, ownerType]);
}

function ArtifactRow({ artifact, showOwner }: { artifact: ArtifactListItem; showOwner: boolean }) {
  const latest = artifact.latest_version;
  const to = `/artifacts/${encodeURIComponent(artifact.id)}`;
  return (
    <tr>
      <td>
        <Link className="cell-link" to={to}>
          {artifact.title}
        </Link>
      </td>
      <td>
        <span className="badge badge-chip">{typeLabel(artifact.content_type)}</span>
      </td>
      {showOwner && (
        <td>
          {artifact.owner.name}
          <div className="sub">{levelLabel(artifact.owner.type)}</div>
        </td>
      )}
      <td className="mono">{latest ? `v${latest.version_number}` : '—'}</td>
      <td>
        {latest ? (
          <>
            {formatDate(latest.created_at)} &middot; {latest.publisher.name}
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="num">{artifact.version_count}</td>
      <td className="mono">
        {artifact.published_version ? `v${artifact.published_version.version_number}` : '—'}
      </td>
      <td>
        <PublicationBadge artifact={artifact} />
      </td>
      <td>
        <div className="row-actions">
          <Link className="btn btn-secondary btn-icon" to={to} title="Open artifact" aria-label={`Open ${artifact.title}`}>
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </td>
    </tr>
  );
}

export function ArtifactsPanel({
  ownerType,
  ownerId,
  ownerName,
  mayPublish,
  clientId,
}: {
  ownerType: ArtifactListOwnerType;
  ownerId: string;
  ownerName: string;
  /** The node payload's `artifact:publish` -- presentation only. */
  mayPublish: boolean;
  /** Client tab only: engagements are offered as owners too. */
  clientId?: string;
}) {
  const navigate = useNavigate();
  const list = useArtifactsForOwner(ownerType, ownerId);
  const owners = useOwnerOptions(ownerType, ownerId, ownerName, mayPublish, clientId);
  const [creating, setCreating] = useState(false);
  const showOwner = ownerType === 'CLIENT';
  const items = list.data?.items ?? [];

  return (
    <>
      <div className="toolbar">
        <span className="hint">Every upload is a new, immutable version. Visibility follows the node an artifact hangs off.</span>
        <div className="spacer" />
        {owners.length > 0 && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" /> New artifact
          </button>
        )}
      </div>

      <div className="card">
        {list.isPending ? (
          <div className="card-body" aria-busy="true" aria-label="Loading artifacts">
            {[0, 1, 2].map((index) => (
              <span key={index} className="skeleton-bar group-skeleton-line" />
            ))}
          </div>
        ) : list.error ? (
          <div className="card-body">
            <div className="empty">
              <h3>Artifacts could not be loaded</h3>
              <p>{list.error instanceof Error ? list.error.message : 'Please try again.'}</p>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => void list.refetch()}>
                Retry
              </button>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="card-body">
            <div className="empty">
              <div className="e-icon">
                <FileText aria-hidden="true" />
              </div>
              <h3>No artifacts yet</h3>
              <p>
                Reports published on {ownerName}
                {showOwner ? ' or its engagements' : ''} appear here, with every version kept as history.
              </p>
            </div>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  {showOwner && <th>Hangs off</th>}
                  <th>Latest version</th>
                  <th>Last upload</th>
                  <th className="num">Versions</th>
                  <th>Published version</th>
                  <th>State</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {items.map((artifact) => (
                  <ArtifactRow key={artifact.id} artifact={artifact} showOwner={showOwner} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && (
        <NewArtifactDialog
          owners={owners}
          onClose={() => setCreating(false)}
          onCreated={(artifactId) => {
            setCreating(false);
            navigate(`/artifacts/${encodeURIComponent(artifactId)}`);
          }}
        />
      )}
    </>
  );
}
